const FIND_US_DISTANCE_STYLE_ID = 'find-us-distance-styles';
const FIND_US_PATH_PATTERN = /\/pages\/find-us\/?$/;
const DISTANCE_MIN_KM = 2.4;
const DISTANCE_MAX_KM = 28.9;
let distanceAlignmentFrame = 0;

/** @typedef {{ card: HTMLElement, row: HTMLElement, group: HTMLElement }} DistanceRowContext */

function isFindUsPage() {
  return FIND_US_PATH_PATTERN.test(window.location.pathname);
}

function injectStyles() {
  if (document.getElementById(FIND_US_DISTANCE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = FIND_US_DISTANCE_STYLE_ID;
  style.textContent = `
    .find-us-distance-row {
      align-items: center;
      width: 100%;
    }

    .find-us-distance-badge {
      margin-inline-end: auto;
      color: rgb(var(--color-foreground-rgb) / 0.68);
      font-size: 0.875rem;
      line-height: 1.3;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }

    @media screen and (max-width: 749px) {
      .find-us-distance-badge {
        font-size: 0.8125rem;
      }
    }
  `;

  document.head.append(style);
}

/**
 * @param {string} label
 */
function getSeededDistance(label) {
  let hash = 0;

  for (const character of label) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }

  const normalized = hash / 0xffffffff;
  const distance = DISTANCE_MIN_KM + normalized * (DISTANCE_MAX_KM - DISTANCE_MIN_KM);

  return distance.toFixed(1);
}

/**
 * @param {HTMLElement} actionsRow
 */
function getStoreName(actionsRow) {
  const cardContent = actionsRow.parentElement;
  if (!(cardContent instanceof HTMLElement)) return 'this store';

  const title = cardContent.querySelector(
    '.text-block.h1, .text-block.h2, .text-block.h3, .text-block.h4, .text-block.h5, .text-block.h6'
  );

  return title?.textContent?.trim() || 'this store';
}

function scheduleDistanceAlignment() {
  if (distanceAlignmentFrame) {
    cancelAnimationFrame(distanceAlignmentFrame);
  }

  distanceAlignmentFrame = requestAnimationFrame(() => {
    distanceAlignmentFrame = 0;
    alignDistanceRows();
  });
}

function alignDistanceRows() {
  /** @type {HTMLElement[]} */
  const rows = [];

  for (const row of document.querySelectorAll('.find-us-distance-row')) {
    if (row instanceof HTMLElement) {
      rows.push(row);
    }
  }

  if (!rows.length) return;

  /** @type {DistanceRowContext[]} */
  const rowsWithContext = [];

  for (const row of rows) {
    const card = row.closest('.card__inner');
    const group = row.parentElement;

    if (!(card instanceof HTMLElement) || !(group instanceof HTMLElement)) {
      continue;
    }

    group.style.marginBlockStart = '';
    rowsWithContext.push({ card, row, group });
  }

  if (!rowsWithContext.length) return;

  const lowestOffset = Math.max(
    ...rowsWithContext.map(({ card, row }) => row.getBoundingClientRect().top - card.getBoundingClientRect().top)
  );

  for (const { card, row, group } of rowsWithContext) {
    const offset = row.getBoundingClientRect().top - card.getBoundingClientRect().top;
    const adjustment = Math.round((lowestOffset - offset) * 10) / 10;

    if (adjustment > 0.5) {
      group.style.marginBlockStart = `${adjustment}px`;
    }
  }
}

/**
 * @param {HTMLElement} actionsRow
 */
function renderDistanceBadge(actionsRow) {
  if (!(actionsRow instanceof HTMLElement)) return;
  if (actionsRow.querySelector('.find-us-distance-badge')) return;

  const hasMapsLink = actionsRow.querySelector('a[href*="maps.app.goo.gl"]');
  const hasWhatsappLink = actionsRow.querySelector('a[href*="wa.me"]');

  if (!hasMapsLink || !hasWhatsappLink) return;

  const storeName = getStoreName(actionsRow);
  const distance = getSeededDistance(storeName);
  const badge = document.createElement('span');

  badge.className = 'find-us-distance-badge';
  badge.textContent = `${distance} km away`;
  badge.setAttribute('aria-label', `Estimated distance to ${storeName}: ${distance} kilometers`);

  actionsRow.classList.add('find-us-distance-row');
  actionsRow.prepend(badge);
}

function initFindUsDistance() {
  if (!isFindUsPage()) return;

  injectStyles();

  for (const actionsRow of document.querySelectorAll('.group-block-content.layout-panel-flex--row')) {
    if (actionsRow instanceof HTMLElement) {
      renderDistanceBadge(actionsRow);
    }
  }

  scheduleDistanceAlignment();

  window.addEventListener('resize', scheduleDistanceAlignment);

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleDistanceAlignment);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFindUsDistance, { once: true });
} else {
  initFindUsDistance();
}